import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
  Check,
  Download,
  Share2,
  RefreshCw,
  Phone,
  TrendingDown,
  Ruler,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { useWizard } from "./WizardContext"
import { Button } from "@/components/ui/button"
import { formatCurrency } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"

export default function ResultsStep() {
  const { state, reset } = useWizard()
  const estimate = state.estimate
  const [showLineItems, setShowLineItems] = useState(false)
  const [animatedTotal, setAnimatedTotal] = useState(0)

  // Animate total cost counting up
  useEffect(() => {
    if (!estimate) return
    const target = estimate.totalCost
    const duration = 1200
    const steps = 40
    const increment = target / steps
    let current = 0
    let step = 0

    const timer = setInterval(() => {
      step++
      current = Math.min(increment * step, target)
      setAnimatedTotal(current)
      if (step >= steps) clearInterval(timer)
    }, duration / steps)

    return () => clearInterval(timer)
  }, [estimate])

  if (!estimate) return null

  const included = [
    "Architectural Shingles",
    "Hip & Ridge Shingles",
    "Starter Strip",
    "Synthetic Underlayment",
    "Ice & Water Shield",
    "Drip Edge",
    "Ridge Vent",
    "Professional Installation",
    "Tear Off & Disposal",
    "Full Cleanup & Haul Away",
  ]

  return (
    <div className="space-y-6">
      {/* Hero result */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">
          <Check className="w-3.5 h-3.5" />
          Estimate Ready
        </div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          Your Roofing Estimate
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {state.formattedAddress}
        </p>
      </motion.div>

      {/* Roof size badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center justify-center gap-3 py-3 px-5 bg-muted rounded-xl"
      >
        <Ruler className="w-5 h-5 text-muted-foreground" />
        <div>
          <span className="text-2xl font-bold text-foreground">{estimate.roofSizeSqft.toLocaleString()}</span>
          <span className="text-sm text-muted-foreground ml-1.5">sq ft estimated</span>
        </div>
      </motion.div>

      {/* Main price card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white overflow-hidden relative"
      >
        {/* Geometric accent */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-primary/20 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="relative">
          <p className="text-sm text-gray-400 font-medium uppercase tracking-wider">Estimated Replacement Cost</p>
          <div className="text-5xl font-bold mt-2 tracking-tight">
            {formatCurrency(animatedTotal)}
          </div>

          <Separator className="my-4 bg-gray-700" />

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Roofing (materials + labor)</span>
              <span className="font-semibold">{formatCurrency(estimate.roofingCost)}</span>
            </div>
            {estimate.addonsCost > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Add-ons</span>
                <span className="font-semibold">{formatCurrency(estimate.addonsCost)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Service fee (10%)</span>
              <span className="font-semibold">{formatCurrency(estimate.serviceFee)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Sales tax (7%)</span>
              <span className="font-semibold">{formatCurrency(estimate.salesTax)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Savings callout */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6 }}
        className="bg-green-50 border-2 border-green-200 rounded-xl p-5 text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <TrendingDown className="w-5 h-5 text-green-600" />
          <span className="text-sm font-bold text-green-800 uppercase tracking-wider">You Save</span>
        </div>
        <div className="text-4xl font-bold text-green-700">
          {formatCurrency(estimate.savings)}
        </div>
        <p className="text-sm text-green-600 mt-1">
          vs. typical contractor price of {formatCurrency(estimate.typicalContractorCost)}
        </p>
      </motion.div>

      {/* Line items expandable */}
      <div className="border-2 border-muted rounded-xl overflow-hidden">
        <button
          onClick={() => setShowLineItems(!showLineItems)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
        >
          <span className="font-bold text-sm text-foreground">Detailed Line Items ({estimate.lineItems.length})</span>
          {showLineItems ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showLineItems && (
          <div className="border-t border-muted">
            <div className="divide-y divide-muted">
              {estimate.lineItems.map((item: any, i: number) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <div>
                    <span className="text-foreground font-medium">{item.name}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {Math.round(item.quantity).toLocaleString()} {item.unit}
                    </span>
                  </div>
                  <span className="font-semibold text-foreground">{formatCurrency(item.totalPrice)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* What's included */}
      <div>
        <h3 className="font-bold text-foreground mb-3">What's included</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {included.map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-foreground">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-bold text-amber-900 text-sm mb-1">Good to know</h4>
        <ul className="text-xs text-amber-800 space-y-1">
          <li>Pricing reflects average local material and labor costs</li>
          <li>Final price may adjust after on-site inspection</li>
          <li>Accessories and upgrades reviewed during inspection</li>
        </ul>
      </div>

      {/* Action buttons */}
      <div className="space-y-3">
        <Button
          className="w-full h-13 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-base transition-all hover:shadow-lg hover:shadow-primary/25"
          onClick={() => window.open("tel:+15138989000")}
        >
          <Phone className="w-5 h-5 mr-2" />
          Schedule Free Inspection
        </Button>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 h-11 rounded-xl font-semibold"
            onClick={() => window.print()}
          >
            <Download className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button
            variant="outline"
            className="flex-1 h-11 rounded-xl font-semibold"
            onClick={() =>
              navigator.share?.({
                title: "My Roof Estimate - Skyline Roofing",
                text: `My roof replacement estimate: ${formatCurrency(estimate.totalCost)}`,
                url: window.location.href,
              })
            }
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        <button
          onClick={reset}
          className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Start a new estimate
        </button>
      </div>
    </div>
  )
}
